import type { CompanyCreateInput, CompanyLegalProfile } from './types';
export function registrationDigits(value: string) {
  return value.replace(/\D/g, '');
}
export function characterLength(value: string) {
  return Array.from(value.trim()).length;
}
export function validMainPhoneNumber(value: string) {
  const normalized = value.trim();
  return (
    normalized === '' ||
    (normalized.length >= 4 && normalized.length <= 50 && /\d/.test(normalized) && /^[0-9+(). -]+$/.test(normalized))
  );
}
export function todayInSeoul() {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Seoul',
  }).format(new Date());
}
export function validFoundedOn(value: string) {
  if (value === '') return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < '0001-01-01' || value > todayInSeoul()) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
}
export function formatBusinessRegistrationNumber(value: string) {
  const digits = registrationDigits(value).slice(0, 10);
  return [digits.slice(0, 3), digits.slice(3, 5), digits.slice(5)].filter(Boolean).join('-');
}
export function formatCorporateRegistrationNumber(value: string) {
  const digits = registrationDigits(value).slice(0, 13);
  return [digits.slice(0, 6), digits.slice(6)].filter(Boolean).join('-');
}
export function validBusinessRegistrationNumber(value: string) {
  const digits = registrationDigits(value);
  if (digits.length === 0) return true;
  if (digits.length !== 10) return false;
  const weights = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  const sum =
    weights.reduce((total, weight, index) => total + Number(digits[index]) * weight, 0) +
    Math.floor((Number(digits[8]) * weights[8]) / 10);
  return (10 - (sum % 10)) % 10 === Number(digits[9]);
}
export function validCorporateRegistrationNumber(value: string) {
  const digits = registrationDigits(value);
  if (digits.length === 0) return true;
  if (digits.length !== 13) return false;
  const sum = Array.from(digits.slice(0, 12)).reduce(
    (total, digit, index) => total + Number(digit) * ((index % 2) + 1),
    0,
  );
  return (10 - (sum % 10)) % 10 === Number(digits[12]);
}
export function businessRegistrationHelper(value: string) {
  const digits = registrationDigits(value);
  if (digits.length === 0) return '선택 입력 · 숫자 10자리';
  if (digits.length !== 10) return '사업자등록번호 숫자 10자리를 입력해 주세요.';
  return validBusinessRegistrationNumber(value)
    ? '확인 가능한 사업자등록번호 형식입니다.'
    : '올바른 사업자등록번호가 아닙니다.';
}
export function corporateRegistrationHelper(value: string) {
  const digits = registrationDigits(value);
  if (digits.length === 0) return '선택 입력 · 숫자 13자리';
  if (digits.length !== 13) return '법인등록번호 숫자 13자리를 입력해 주세요.';
  return validCorporateRegistrationNumber(value)
    ? '확인 가능한 법인등록번호 형식입니다.'
    : '올바른 법인등록번호가 아닙니다.';
}
export function companyLegalKey(profile?: CompanyLegalProfile) {
  if (!profile) return '';
  return JSON.stringify({
    descriptor: profile.descriptor.trim(),
    legalName: profile.legalName.trim(),
    mainPhoneNumber: profile.mainPhoneNumber.trim(),
    foundedOn: profile.foundedOn,
    registeredAddress: profile.registeredAddress.trim(),
    businessRegistrationNumber: registrationDigits(profile.businessRegistrationNumber),
    corporateRegistrationNumber: registrationDigits(profile.corporateRegistrationNumber),
    representativeTitle: profile.representativeTitle.trim(),
    representativeName: profile.representativeName.trim(),
  });
}
export function normalizeCompanyLegalProfile(profile: CompanyLegalProfile): CompanyLegalProfile {
  return {
    ...profile,
    isPrimary: profile.isPrimary ?? false,
    mainPhoneNumber: (profile.mainPhoneNumber ?? '').trim(),
    foundedOn: profile.foundedOn ?? '',
    businessRegistrationNumber: formatBusinessRegistrationNumber(profile.businessRegistrationNumber),
    corporateRegistrationNumber: formatCorporateRegistrationNumber(profile.corporateRegistrationNumber),
  };
}
export function companyCreateProfile(input: CompanyCreateInput): CompanyLegalProfile {
  return {
    ...input,
    companyId: '',
    isPrimary: false,
    sealConfigured: false,
    version: 0,
  };
}
export function validCompanyLegalProfile(profile?: CompanyLegalProfile) {
  return Boolean(
    profile &&
    characterLength(profile.descriptor) >= 1 &&
    characterLength(profile.descriptor) <= 200 &&
    characterLength(profile.legalName) >= 1 &&
    characterLength(profile.legalName) <= 200 &&
    validMainPhoneNumber(profile.mainPhoneNumber) &&
    validFoundedOn(profile.foundedOn) &&
    characterLength(profile.registeredAddress) >= 1 &&
    characterLength(profile.registeredAddress) <= 500 &&
    validBusinessRegistrationNumber(profile.businessRegistrationNumber) &&
    validCorporateRegistrationNumber(profile.corporateRegistrationNumber) &&
    characterLength(profile.representativeTitle) >= 1 &&
    characterLength(profile.representativeTitle) <= 80 &&
    characterLength(profile.representativeName) >= 1 &&
    characterLength(profile.representativeName) <= 100,
  );
}
